import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../api/admin.api';
import Button from '../../components/common/Button';
import CatalogOptionsField from '../../components/admin/CatalogOptionsField';
import {
  EYEGLASSES_PRESCRIPTION_OPTIONS,
  FILTER_GENDERS,
  FILTER_MATERIALS,
  NAV_CATEGORIES,
  PRESENT_TYPE_OPTIONS,
} from '../../constants/catalog';
import { getUploadErrorMessage, prepareImageForUpload } from '../../utils/imageUpload';
import { getProductMaterials } from '../../utils/catalogOptions';
import { syncSalePricingFields } from '../../utils/productPricing';
import './CreateProduct.css';

const titleize = (value) => value
  .split(/[-_]/g)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const BOX_IMAGE_FIELDS = [
  { id: 'basic', label: 'Basic box photo', priceLabel: 'Basic box price', defaultPrice: '0' },
  { id: 'good', label: 'Good box photo', priceLabel: 'Good box price', defaultPrice: '15' },
  { id: 'brand', label: 'Sifrifana brand box photo', priceLabel: 'Sifrifana brand box price', defaultPrice: '20' },
];

const CreateProduct = () => {
  const navigate = useNavigate();
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [images, setImages] = useState([]);
  const [boxImages, setBoxImages] = useState({
    basic: '',
    good: '',
    brand: '',
  });
  const [boxPrices, setBoxPrices] = useState({
    basic: '0',
    good: '15',
    brand: '20',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingBoxId, setUploadingBoxId] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    specialCode: '',
    description: '',
    youtubeVideoUrl: '',
    price: '',
    category: '',
    presentType: '',
    brand: '',
    gender: '',
    materials: [],
    salePercentage: '',
    salePrice: '',
    stock: '',
    rating: '',
    prescriptionOptions: [],
    homeSectionKeys: [],
  });

  useEffect(() => {
    const loadCatalogProducts = async () => {
      try {
        const data = await adminAPI.getProducts();
        const products = data?.items || data?.products || data?.data || data || [];
        setCatalogProducts(Array.isArray(products) ? products : []);
      } catch (error) {
        console.error('Failed to load product options:', error);
        setCatalogProducts([]);
      }
    };

    loadCatalogProducts();
  }, []);

  const brandOptions = useMemo(() => Array.from(new Set(
    catalogProducts
      .map((product) => String(product.brand || '').trim())
      .filter(Boolean)
  )).sort((first, second) => first.localeCompare(second)), [catalogProducts]);

  const materialOptions = useMemo(() => Array.from(new Set([
    ...FILTER_MATERIALS,
    ...catalogProducts.flatMap(getProductMaterials),
  ])).sort((first, second) => first.localeCompare(second)), [catalogProducts]);

  const isEyeglasses = formData.category === 'eyeglasses';
  const isPresents = formData.category === 'presents';
  const requiresGender = formData.category !== 'knives';

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      ...(['price', 'salePercentage', 'salePrice'].includes(name)
        ? syncSalePricingFields(current, name, value)
        : { [name]: value }),
      ...(name === 'category' && value !== 'eyeglasses' ? { prescriptionOptions: [] } : {}),
      ...(name === 'category' && value !== 'presents' ? { presentType: '' } : {}),
      ...(name === 'category' && value === 'knives' ? { gender: '' } : {}),
      ...(name === 'category' && value && current.homeSectionKeys.length === 0 ? { homeSectionKeys: [value] } : {}),
    }));
  };

  const toggleHomeSection = (sectionKey) => {
    setFormData((current) => ({
      ...current,
      homeSectionKeys: current.homeSectionKeys.includes(sectionKey)
        ? current.homeSectionKeys.filter((key) => key !== sectionKey)
        : [...current.homeSectionKeys, sectionKey],
    }));
  };

  const togglePrescription = (prescription) => {
    setFormData((current) => ({
      ...current,
      prescriptionOptions: current.prescriptionOptions.includes(prescription)
        ? current.prescriptionOptions.filter((option) => option !== prescription)
        : [...current.prescriptionOptions, prescription],
    }));
  };

  const uploadFiles = async (files) => {
    const nextFiles = Array.from(files || []);
    if (!nextFiles.length) {
      return;
    }

    setUploading(true);

    try {
      const uploadedImages = [];

      for (const file of nextFiles) {
        const uploadPayload = await prepareImageForUpload(file);
        const { url, absoluteUrl } = await adminAPI.uploadImage(uploadPayload);
        uploadedImages.push(url || absoluteUrl);
      }

      setImages((current) => [...current, ...uploadedImages]);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(getUploadErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const uploadBoxImage = async (boxId, file) => {
    if (!file) {
      return;
    }

    setUploadingBoxId(boxId);

    try {
      const uploadPayload = await prepareImageForUpload(file);
      const { url, absoluteUrl } = await adminAPI.uploadImage(uploadPayload);
      setBoxImages((current) => ({
        ...current,
        [boxId]: url || absoluteUrl,
      }));
    } catch (error) {
      console.error('Box image upload failed:', error);
      alert(getUploadErrorMessage(error, 'Box image upload failed'));
    } finally {
      setUploadingBoxId('');
    }
  };

  const handleBoxPriceChange = (boxId, value) => {
    setBoxPrices((current) => ({
      ...current,
      [boxId]: boxId === 'basic' ? '0' : value,
    }));
  };

  const makeMainImage = (index) => {
    setImages((current) => {
      if (index <= 0) {
        return current;
      }

      const nextImages = [...current];
      const [selectedImage] = nextImages.splice(index, 1);
      nextImages.unshift(selectedImage);
      return nextImages;
    });
  };

  const moveImage = (index, direction) => {
    setImages((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const nextImages = [...current];
      [nextImages[index], nextImages[nextIndex]] = [nextImages[nextIndex], nextImages[index]];
      return nextImages;
    });
  };

  const removeImage = (index) => {
    setImages((current) => current.filter((_, imageIndex) => imageIndex !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);

    try {
      if (!formData.homeSectionKeys.length) {
        alert('Choose at least one homepage section for this product');
        setLoading(false);
        return;
      }

      if (isPresents && !formData.presentType) {
        alert('Choose a gift type for presents');
        setLoading(false);
        return;
      }

      if (!formData.materials.length) {
        alert('Add at least one catalog filter option');
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title,
        specialCode: formData.specialCode,
        description: formData.description,
        youtubeVideoUrl: formData.youtubeVideoUrl,
        price: Number(formData.price),
        category: formData.category,
        presentType: isPresents ? formData.presentType : '',
        brand: formData.brand,
        gender: formData.gender,
        material: formData.materials[0],
        materials: formData.materials,
        salePercentage: Number(formData.salePercentage || 0),
        salePrice: Number(formData.salePrice || 0),
        stock: Number(formData.stock),
        rating: Number(formData.rating || 0),
        prescriptionOptions: isEyeglasses ? formData.prescriptionOptions : [],
        homeSectionKeys: formData.homeSectionKeys,
        images,
        boxImages,
        boxPrices: {
          basic: 0,
          good: Number(boxPrices.good || 0),
          brand: Number(boxPrices.brand || 0),
        },
      };

      await adminAPI.createProduct(payload);
      alert('Product created successfully!');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error.response?.data || error);
      alert(error.response?.data?.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-product">
      <div className="create-product__header">
        <h1>Create New Product</h1>
      </div>

      <form onSubmit={handleSubmit} className="create-product__form">
        <section className="create-product__section">
          <h2>Product</h2>
          <div className="create-product__grid">
            <label className="create-product__field create-product__field--wide">
              <span>Product title</span>
              <input
                name="title"
                value={formData.title}
                onChange={handleFieldChange}
                required
              />
            </label>

            <label className="create-product__field create-product__field--wide">
              <span>Product special code</span>
              <input
                name="specialCode"
                value={formData.specialCode}
                onChange={handleFieldChange}
                placeholder="Example: KMLS 2.5"
              />
            </label>

            <label className="create-product__field create-product__field--wide">
              <span>Description</span>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFieldChange}
                rows={4}
                required
              />
            </label>

            <label className="create-product__field create-product__field--wide">
              <span>YouTube video link</span>
              <input
                name="youtubeVideoUrl"
                type="url"
                value={formData.youtubeVideoUrl}
                onChange={handleFieldChange}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>
          </div>
        </section>

        <section className="create-product__section">
          <h2>Photos</h2>
          <div className="create-product__upload-row">
            <label className="create-product__upload">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => {
                  uploadFiles(event.target.files);
                  event.target.value = '';
                }}
              />
              <span>{uploading ? 'Uploading...' : 'Upload product photos'}</span>
            </label>
            <small>The first image is the main product photo.</small>
          </div>

          {images.length > 0 ? (
            <div className="create-product__images">
              {images.map((image, index) => (
                <article key={`${image}-${index}`} className="create-product__image">
                  <img src={image} alt="" />
                  <div className="create-product__image-meta">
                    <strong>{index === 0 ? 'Main photo' : `Photo ${index + 1}`}</strong>
                    <span>{index === 0 ? 'Shown first on product page' : 'Gallery photo'}</span>
                  </div>
                  <div className="create-product__image-actions">
                    <button type="button" onClick={() => makeMainImage(index)} disabled={index === 0}>
                      Main
                    </button>
                    <button type="button" onClick={() => moveImage(index, -1)} disabled={index === 0}>
                      Up
                    </button>
                    <button type="button" onClick={() => moveImage(index, 1)} disabled={index === images.length - 1}>
                      Down
                    </button>
                    <button type="button" onClick={() => removeImage(index)}>
                      Remove
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="create-product__empty-images">No photos uploaded yet.</p>
          )}
        </section>

        <section className="create-product__section">
          <h2>Box photos</h2>
          <p className="create-product__hint">Upload photos only for box options currently in stock. Missing photos are treated as out of stock. Basic box price is always zero.</p>

          <div className="create-product__box-images">
            {BOX_IMAGE_FIELDS.map((field) => (
              <article key={field.id} className="create-product__box-card">
                <div className="create-product__box-preview">
                  {boxImages[field.id] ? (
                    <img src={boxImages[field.id]} alt="" />
                  ) : (
                    <span>No photo yet</span>
                  )}
                </div>

                <div className="create-product__box-copy">
                  <strong>{field.label}</strong>
                  <small>{boxImages[field.id] ? 'Available' : 'Out of stock'}</small>
                </div>

                <label className="create-product__box-price">
                  <span>{field.priceLabel}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={field.id === 'basic' ? '0' : boxPrices[field.id]}
                    onChange={(event) => handleBoxPriceChange(field.id, event.target.value)}
                    readOnly={field.id === 'basic'}
                  />
                </label>

                <div className="create-product__box-actions">
                  <label className="create-product__upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        uploadBoxImage(field.id, event.target.files?.[0]);
                        event.target.value = '';
                      }}
                    />
                    <span>{uploadingBoxId === field.id ? 'Uploading...' : (boxImages[field.id] ? 'Replace photo' : 'Upload photo')}</span>
                  </label>
                  {boxImages[field.id] && (
                    <button
                      type="button"
                      onClick={() => setBoxImages((current) => ({ ...current, [field.id]: '' }))}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="create-product__section">
          <h2>Catalog</h2>
          <div className="create-product__grid">
            <label className="create-product__field">
              <span>Category</span>
              <select name="category" value={formData.category} onChange={handleFieldChange} required>
                <option value="">Select category</option>
                {NAV_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {titleize(category)}
                  </option>
                ))}
              </select>
            </label>

            <label className="create-product__field">
              <span>Brand</span>
              <input
                name="brand"
                value={formData.brand}
                onChange={handleFieldChange}
                list="brand-options"
                placeholder="Select or type new brand"
                autoComplete="off"
              />
              <datalist id="brand-options">
                {brandOptions.map((brand) => (
                  <option key={brand} value={brand} />
                ))}
              </datalist>
              {formData.brand && (
                <button
                  type="button"
                  className="create-product__inline-action"
                  onClick={() => setFormData((current) => ({ ...current, brand: '' }))}
                >
                  Remove brand
                </button>
              )}
            </label>

            {isPresents && (
              <label className="create-product__field">
                <span>Gift type</span>
                <select name="presentType" value={formData.presentType} onChange={handleFieldChange} required>
                  <option value="">Select gift type</option>
                  {PRESENT_TYPE_OPTIONS.map((presentType) => (
                    <option key={presentType.value} value={presentType.value}>
                      {presentType.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {requiresGender && (
              <label className="create-product__field">
                <span>Gender</span>
                <select name="gender" value={formData.gender} onChange={handleFieldChange} required>
                  <option value="">Select gender</option>
                  {FILTER_GENDERS.map((gender) => (
                    <option key={gender} value={gender}>{gender}</option>
                  ))}
                </select>
              </label>
            )}

            <CatalogOptionsField
              values={formData.materials}
              suggestions={materialOptions}
              listId="material-options"
              onChange={(materials) => setFormData((current) => ({ ...current, materials }))}
            />
          </div>

          {isEyeglasses && (
            <fieldset className="create-product__prescriptions">
              <legend>Prescription options</legend>
              {EYEGLASSES_PRESCRIPTION_OPTIONS.map((prescription) => (
                <label key={prescription}>
                  <input
                    type="checkbox"
                    checked={formData.prescriptionOptions.includes(prescription)}
                    onChange={() => togglePrescription(prescription)}
                  />
                  <span>{prescription}</span>
                </label>
              ))}
            </fieldset>
          )}
        </section>

        <section className="create-product__section">
          <h2>Homepage sections</h2>
          <p className="create-product__hint">Choose where this product can be shown on the homepage. The newest selected product is placed first in those section lists.</p>

          <fieldset className="create-product__home-sections">
            <legend>Show this product in</legend>
            {NAV_CATEGORIES.map((category) => (
              <label key={category}>
                <input
                  type="checkbox"
                  checked={formData.homeSectionKeys.includes(category)}
                  onChange={() => toggleHomeSection(category)}
                />
                <span>{titleize(category)}</span>
              </label>
            ))}
          </fieldset>
        </section>

        <section className="create-product__section">
          <h2>Pricing and stock</h2>
          <div className="create-product__grid">
            <label className="create-product__field">
              <span>Price</span>
              <input
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={handleFieldChange}
                required
              />
            </label>

            <label className="create-product__field">
              <span>Sale percentage</span>
              <input
                name="salePercentage"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.salePercentage}
                onChange={handleFieldChange}
              />
            </label>

            <label className="create-product__field">
              <span>Sale price</span>
              <input
                name="salePrice"
                type="number"
                min="0"
                step="0.01"
                value={formData.salePrice}
                onChange={handleFieldChange}
                placeholder="Enter desired sale price"
              />
              <small>Enter sale percentage or sale price; the other field updates automatically.</small>
            </label>

            <label className="create-product__field">
              <span>Amount in stock</span>
              <input
                name="stock"
                type="number"
                min="0"
                step="1"
                value={formData.stock}
                onChange={handleFieldChange}
                required
              />
            </label>

            <label className="create-product__field">
              <span>Rating</span>
              <input
                name="rating"
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={formData.rating}
                onChange={handleFieldChange}
              />
            </label>
          </div>
        </section>

        <div className="create-product__actions">
          <Button type="submit" disabled={loading || uploading || Boolean(uploadingBoxId)}>
            {loading ? 'Creating...' : 'Create Product'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/products')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateProduct;
